import React, { useState } from 'react';
import styled from 'styled-components';

const SupportContainer = styled.div`
  padding: 20px 0;
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 40px;
  text-align: center;
  
  h2 {
    margin: 0 0 12px 0;
    color: #333;
    font-size: 32px;
    font-weight: 700;
  }
  
  .subtitle {
    font-size: 18px;
    color: #666;
    line-height: 1.5;
    max-width: 600px;
    margin: 0 auto;
  }
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 50px;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  border: 1px solid #f0f0f0;
  
  &.support-phone {
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
    color: white;
    text-align: center;
    
    @media (min-width: 768px) {
      grid-column: span 2;
    }
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 30px;
  
  .section-icon {
    font-size: 48px;
    margin-bottom: 16px;
    display: block;
  }
  
  h3 {
    margin: 0 0 12px 0;
    font-size: 24px;
    font-weight: 700;
  }
  
  .section-subtitle {
    font-size: 16px;
    opacity: 0.8;
    line-height: 1.5;
  }
`;

const PhoneButton = styled.a`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 16px 32px;
  border-radius: 12px;
  text-decoration: none;
  font-size: 20px;
  font-weight: 600;
  margin: 20px 0;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
  }
`;

const SupportHours = styled.div`
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 20px;
  margin-top: 20px;
  
  h4 {
    margin: 0 0 12px 0;
    font-size: 18px;
    font-weight: 600;
  }
  
  p {
    margin: 4px 0;
    opacity: 0.9;
  }
`;

const FeedbackForm = styled.form`
  .form-group {
    margin-bottom: 20px;
    
    label {
      display: block;
      font-size: 14px;
      font-weight: 600;
      margin-bottom: 8px;
      color: #333;
    }
    
    input, select, textarea {
      width: 100%;
      padding: 12px 16px;
      border: 1px solid #ddd;
      border-radius: 8px;
      background: white;
      color: #333;
      font-size: 14px;
      transition: border-color 0.2s ease;
      
      &:focus {
        outline: none;
        border-color: #007AFF;
        box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
      }
    }
    
    textarea {
      min-height: 100px;
      resize: vertical;
      font-family: inherit;
    }
  }
  
  .submit-button {
    background: #007AFF;
    color: white;
    border: none;
    padding: 14px 28px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover:not(:disabled) {
      background: #0056b3;
      transform: translateY(-1px);
    }
    
    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      transform: none;
    }
  }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
  color: #155724;
  text-align: center;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
  color: #721c24;
  text-align: center;
`;

const Support = () => {
  const [bugSubmitted, setBugSubmitted] = useState(false);
  const [featureSubmitted, setFeatureSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState({ bug: false, feature: false });
  const [errors, setErrors] = useState({ bug: '', feature: '' });

  const handleBugSubmit = async (e) => {
    e.preventDefault();
    setSubmitting({ ...submitting, bug: true });
    setErrors({ ...errors, bug: '' });

    const formData = new FormData(e.target);
    const data = {
      summary: formData.get('summary'),
      description: formData.get('description'),
      steps: formData.get('steps'),
      priority: formData.get('priority'),
      contact: formData.get('contact')
    };

    try {
      const emailBody = `
Bug Report

Summary: ${data.summary}
Priority: ${data.priority}

Description:
${data.description}

Steps to Reproduce:
${data.steps || 'Not provided'}

Contact (optional): ${data.contact || 'Not provided'}

Submitted: ${new Date().toLocaleString()}
Browser: ${navigator.userAgent}
URL: ${window.location.href}
      `.trim();

      const subject = encodeURIComponent('ShowingBooker | Bug Report');
      const body = encodeURIComponent(emailBody);
      const mailtoLink = `mailto:hello@showingbooker.com?subject=${subject}&body=${body}`;
      
      window.open(mailtoLink, '_blank');
      
      setBugSubmitted(true);
      e.target.reset();
      
    } catch (error) {
      setErrors({ ...errors, bug: 'Failed to submit bug report. Please try again.' });
    } finally {
      setSubmitting({ ...submitting, bug: false });
    }
  };

  const handleFeatureSubmit = async (e) => {
    e.preventDefault();
    setSubmitting({ ...submitting, feature: true });
    setErrors({ ...errors, feature: '' });

    const formData = new FormData(e.target);
    const data = {
      title: formData.get('title'),
      description: formData.get('description'),
      usecase: formData.get('usecase'),
      priority: formData.get('priority'),
      contact: formData.get('contact')
    };

    try {
      const emailBody = `
Feature Request

Title: ${data.title}
Priority: ${data.priority}

Description:
${data.description}

Use Case / Why Needed:
${data.usecase}

Contact (optional): ${data.contact || 'Not provided'}

Submitted: ${new Date().toLocaleString()}
      `.trim();

      const subject = encodeURIComponent('ShowingBooker | Feature Request');
      const body = encodeURIComponent(emailBody);
      const mailtoLink = `mailto:david@codexdeus.com?subject=${subject}&body=${body}`;
      
      window.open(mailtoLink, '_blank');
      
      setFeatureSubmitted(true);
      e.target.reset();
      
    } catch (error) {
      setErrors({ ...errors, feature: 'Failed to submit feature request. Please try again.' });
    } finally {
      setSubmitting({ ...submitting, feature: false });
    }
  };

  return (
    <SupportContainer>
      <PageHeader>
        <h2>📞 Support</h2>
        <div className="subtitle">
          Need help with ShowingBooker? We're here to assist you with any questions, 
          technical issues, or feature requests.
        </div>
      </PageHeader>

      <SectionGrid>
        {/* Phone Support - Featured Section */}
        <Section className="support-phone">
          <SectionHeader>
            <span className="section-icon">📞</span>
            <h3>Call Support</h3>
            <div className="section-subtitle">
              Speak directly with our support team for immediate assistance
            </div>
          </SectionHeader>

          <PhoneButton href="tel:+13109065459">
            📞 Call or Text (310) 906-5459
          </PhoneButton>

          <SupportHours>
            <h4>Support Hours</h4>
            <p><strong>Monday - Friday:</strong> 9:00 AM - 6:00 PM PST</p>
            <p><strong>Saturday:</strong> 10:00 AM - 4:00 PM PST</p>
            <p><strong>Sunday:</strong> Closed</p>
            <p style={{ marginTop: '12px', fontSize: '14px' }}>
              <strong>Emergency Support:</strong> Available 24/7 for urgent technical issues
            </p>
          </SupportHours>
        </Section>

        {/* Bug Reporting Section */}
        <Section>
          <SectionHeader>
            <span className="section-icon">🐛</span>
            <h3>Report a Bug</h3>
            <div className="section-subtitle">
              Found something that's not working right? Help us fix it!
            </div>
          </SectionHeader>

          <FeedbackForm onSubmit={handleBugSubmit}>
            <div className="form-group">
              <label>Bug Summary *</label>
              <input type="text" name="summary" placeholder="Brief description of the issue" required />
            </div>
            
            <div className="form-group">
              <label>Priority Level *</label>
              <select name="priority" required>
                <option value="">Select priority</option>
                <option value="low">Low - Minor inconvenience</option>
                <option value="medium">Medium - Affects functionality</option>
                <option value="high">High - Prevents important tasks</option>
                <option value="critical">Critical - App unusable</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>Detailed Description *</label>
              <textarea name="description" placeholder="What happened? What did you expect to happen?" required></textarea>
            </div>
            
            <div className="form-group">
              <label>Steps to Reproduce</label>
              <textarea name="steps" placeholder="1. Go to... 2. Click on... 3. See error..."></textarea>
            </div>
            
            <div className="form-group">
              <label>Contact Info (Optional)</label>
              <input type="text" name="contact" placeholder="Email or phone if we need to follow up" />
            </div>
            
            <button type="submit" className="submit-button" disabled={submitting.bug}>
              {submitting.bug ? 'Submitting...' : 'Submit Bug Report'}
            </button>
            
            {bugSubmitted && (
              <SuccessMessage>
                ✅ Thank you! Your bug report has been submitted. We'll investigate and fix it ASAP.
              </SuccessMessage>
            )}
            
            {errors.bug && <ErrorMessage>{errors.bug}</ErrorMessage>}
          </FeedbackForm>
        </Section>

        {/* Feature Request Section */}
        <Section>
          <SectionHeader>
            <span className="section-icon">💡</span>
            <h3>Request a Feature</h3>
            <div className="section-subtitle">
              Have an idea to make ShowingBooker better? We'd love to hear it!
            </div>
          </SectionHeader>

          <FeedbackForm onSubmit={handleFeatureSubmit}>
            <div className="form-group">
              <label>Feature Title *</label>
              <input type="text" name="title" placeholder="Short name for your feature idea" required />
            </div>
            
            <div className="form-group">
              <label>Priority / Impact *</label>
              <select name="priority" required>
                <option value="">How important is this?</option>
                <option value="nice-to-have">Nice to have</option>
                <option value="useful">Would be useful</option>
                <option value="important">Important for my workflow</option>
                <option value="critical">Critical for my business</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>Feature Description *</label>
              <textarea name="description" placeholder="Describe what you'd like to see and how it would work..." required></textarea>
            </div>
            
            <div className="form-group">
              <label>Use Case / Why Needed *</label>
              <textarea name="usecase" placeholder="What problem would this solve? How would you use it?" required></textarea>
            </div>
            
            <div className="form-group">
              <label>Contact Info (Optional)</label>
              <input type="text" name="contact" placeholder="Email or phone if we want to discuss further" />
            </div>
            
            <button type="submit" className="submit-button" disabled={submitting.feature}>
              {submitting.feature ? 'Submitting...' : 'Submit Feature Request'}
            </button>
            
            {featureSubmitted && (
              <SuccessMessage>
                ✅ Thank you! Your feature request has been submitted. We'll review it and consider it for future releases.
              </SuccessMessage>
            )}
            
            {errors.feature && <ErrorMessage>{errors.feature}</ErrorMessage>}
          </FeedbackForm>
        </Section>
      </SectionGrid>
    </SupportContainer>
  );
};

export default Support;